"use client"

import { createContext, useContext, useState, useCallback, useEffect, useRef } from "react"
import { useRouter, usePathname } from "next/navigation"
import { ArrowLeft, BookOpen, Loader2, MessagesSquare, Mic, Settings2, Video } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Switch } from "@/components/ui/switch"

const ControlBarContext = createContext(null)

export function ControlBarProvider({ children, accessTier: initialAccessTier = null, user = null, questions = [] }) {
  const [cta, setCta] = useState(null)
  const [stopHandler, setStopHandler] = useState(null)
  const [busy, setBusy] = useState(false)
  const [mode, setMode] = useState(() => {
    if (typeof window === "undefined") return "live"
    return localStorage.getItem("station:mode") || "live"
  })
  const [readingTime, setReadingTime] = useState(() => {
    if (typeof window === "undefined") return 120
    const v = parseInt(localStorage.getItem("station:readingTime"), 10)
    return isNaN(v) ? 120 : v
  })
  const [interviewTime, setInterviewTime] = useState(() => {
    if (typeof window === "undefined") return 480
    const v = parseInt(localStorage.getItem("station:interviewTime"), 10)
    return isNaN(v) ? 480 : v
  })
  const [prepEnabled, setPrepEnabled] = useState(() => {
    if (typeof window === "undefined") return false
    return localStorage.getItem("station:prepEnabled") === "true"
  })
  const [language, setLanguage] = useState(() => {
    if (typeof window === "undefined") return "en"
    return localStorage.getItem("station:language") || "en"
  })
  const [accessTier, setAccessTier] = useState(initialAccessTier)

  // Persist station settings to localStorage
  useEffect(() => { localStorage.setItem("station:mode", mode) }, [mode])
  useEffect(() => { localStorage.setItem("station:readingTime", String(readingTime)) }, [readingTime])
  useEffect(() => { localStorage.setItem("station:interviewTime", String(interviewTime)) }, [interviewTime])
  useEffect(() => { localStorage.setItem("station:prepEnabled", String(prepEnabled)) }, [prepEnabled])
  useEffect(() => { localStorage.setItem("station:language", language) }, [language])

  // Sync with server prop when layout re-renders (e.g. after revalidatePath)
  useEffect(() => {
    setAccessTier(initialAccessTier)
  }, [initialAccessTier])

  const registerCta = useCallback((descriptor) => {
    setCta(descriptor)
  }, [])

  const registerStop = useCallback((fn) => {
    setStopHandler(fn ? () => fn : null)
  }, [])

  const registerBusy = useCallback((val) => {
    setBusy(val)
  }, [])

  return (
    <ControlBarContext.Provider
      value={{
        cta,
        registerCta,
        isInterviewActive: !!stopHandler,
        stopHandler,
        registerStop,
        busy,
        registerBusy,
        mode,
        setMode,
        readingTime,
        setReadingTime,
        interviewTime,
        setInterviewTime,
        prepEnabled,
        setPrepEnabled,
        language,
        setLanguage,
        accessTier,
        setAccessTier,
        user,
        questions,
      }}
    >
      {children}
    </ControlBarContext.Provider>
  )
}

export function useControlBar() {
  const ctx = useContext(ControlBarContext)
  if (!ctx) throw new Error("useControlBar must be used within ControlBarProvider")
  return ctx
}

const MODE_OPTIONS = [
  { value: "live", label: "Live", icon: MessagesSquare, description: "Conversational interview with AI" },
  { value: "async", label: "Async", icon: Video, description: "Self-recorded response" },
]

function ModeToggleInline() {
  const { mode, setMode, isInterviewActive, busy, accessTier, user } = useControlBar()
  const isLimited = accessTier?.tier === "limited"
  const disabled = isInterviewActive || busy || isLimited || !user

  const tooltipText = !user
    ? "Sign in to toggle interview mode"
    : isLimited
      ? "Upgrade to access live interview mode"
      : "Live: AI interviewer · Async: self-recorded"

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <div
          className={`flex rounded-full bg-muted p-0.5 ${disabled ? "opacity-50" : ""} ${(isInterviewActive || busy) ? "pointer-events-none" : ""}`}
        >
          {MODE_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              type="button"
              disabled={disabled}
              onClick={() => setMode(opt.value)}
              className={`flex items-center gap-1.5 rounded-full px-3.5 py-1 text-sm font-medium transition-all ${
                mode === opt.value
                  ? "bg-background text-foreground shadow-sm"
                  : "text-muted-foreground"
              }`}
            >
              <opt.icon className="h-3.5 w-3.5" />
              {opt.label}
            </button>
          ))}
        </div>
      </TooltipTrigger>
      <TooltipContent side="top" sideOffset={8}>
        {tooltipText}
      </TooltipContent>
    </Tooltip>
  )
}

function ModeToggleCompact({ disabled }) {
  const { mode, setMode } = useControlBar()

  return (
    <div className={`flex rounded-full bg-muted p-0.5 ${disabled ? "opacity-50 pointer-events-none" : ""}`}>
      {MODE_OPTIONS.map((opt) => (
        <button
          key={opt.value}
          type="button"
          disabled={disabled}
          onClick={() => setMode(opt.value)}
          className={`flex flex-1 items-center justify-center gap-1.5 rounded-full py-1 text-sm font-medium transition-all ${
            mode === opt.value
              ? "bg-background text-foreground shadow-sm"
              : "text-muted-foreground"
          }`}
        >
          <opt.icon className="h-3.5 w-3.5" />
          {opt.label}
        </button>
      ))}
    </div>
  )
}

const LANGUAGE_OPTIONS = [
  { value: "en", label: "EN" },
  { value: "fr", label: "FR" },
]

function LanguageToggleCompact({ disabled }) {
  const { language, setLanguage } = useControlBar()

  return (
    <div className={`flex rounded-full bg-muted p-0.5 ${disabled ? "opacity-50 pointer-events-none" : ""}`}>
      {LANGUAGE_OPTIONS.map((opt) => (
        <button
          key={opt.value}
          type="button"
          disabled={disabled}
          onClick={() => setLanguage(opt.value)}
          className={`flex flex-1 items-center justify-center rounded-full px-3 py-1 text-sm font-medium transition-all ${
            language === opt.value
              ? "bg-background text-foreground shadow-sm"
              : "text-muted-foreground"
          }`}
        >
          {opt.label}
        </button>
      ))}
    </div>
  )
}

function StationSettings({ variant = "full" }) {
  const { readingTime, setReadingTime, interviewTime, setInterviewTime, prepEnabled, setPrepEnabled, language, setLanguage, isInterviewActive, busy, user } = useControlBar()
  const disabled = isInterviewActive || busy || !user
  const Icon = Settings2
  const tooltip = !user ? "Sign in to modify station settings" : "Station settings"

  return (
    <Popover>
      <Tooltip>
        <TooltipTrigger asChild>
          <PopoverTrigger asChild disabled={disabled}>
            <button
              type="button"
              className={`flex h-8 w-8 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-muted hover:text-foreground ${disabled ? "opacity-50" : ""}`}
            >
              <Icon className="h-4 w-4" />
            </button>
          </PopoverTrigger>
        </TooltipTrigger>
        <TooltipContent side="top" sideOffset={8}>{tooltip}</TooltipContent>
      </Tooltip>
      <PopoverContent side="top" sideOffset={12} className="w-64">
        <div className="flex flex-col gap-4">
          <p className="flex items-center gap-1.5 text-sm font-medium"><Settings2 className="h-3.5 w-3.5" />Station settings</p>
          {/* Mode toggle — visible only on mobile inside popover */}
          <div className="flex flex-col gap-2 sm:hidden">
            <p className="text-xs text-muted-foreground">Mode</p>
            <ModeToggleCompact disabled={disabled} />
          </div>
          <div className="flex flex-col gap-2">
            <p className="text-xs text-muted-foreground">Timers</p>
            <div className="grid grid-cols-[1fr_auto_auto_auto] items-center gap-x-2 gap-y-3">
              <Label htmlFor="prep-toggle" className={`flex items-center gap-1.5 text-sm text-muted-foreground ${!prepEnabled ? "opacity-40" : ""}`}><BookOpen className="h-3.5 w-3.5" />Prompt</Label>
              <Input
                id="reading-time"
                type="number"
                min={0.5}
                max={5}
                step={0.5}
                disabled={!prepEnabled}
                value={readingTime / 60}
                onChange={(e) => {
                  const val = parseFloat(e.target.value)
                  if (!isNaN(val) && val >= 0.5 && val <= 5) setReadingTime(Math.round(val * 60))
                }}
                className={`h-8 w-16 text-center ${!prepEnabled ? "opacity-40" : ""}`}
              />
              <span className={`text-xs text-muted-foreground ${!prepEnabled ? "opacity-40" : ""}`}>min</span>
              <Switch
                id="prep-toggle"
                checked={prepEnabled}
                onCheckedChange={setPrepEnabled}
              />

              <Label htmlFor="interview-time" className="flex items-center gap-1.5 text-sm text-muted-foreground"><Mic className="h-3.5 w-3.5" />Discussion</Label>
              <Input
                id="interview-time"
                type="number"
                min={1}
                max={15}
                step={1}
                value={interviewTime / 60}
                onChange={(e) => {
                  const val = parseFloat(e.target.value)
                  if (!isNaN(val) && val >= 1 && val <= 15) setInterviewTime(Math.round(val * 60))
                }}
                className="h-8 w-16 text-center"
              />
              <span className="text-xs text-muted-foreground">min</span>
              <div />
            </div>
          </div>
          <div className="flex flex-col gap-2">
            <p className="text-xs text-muted-foreground">Language</p>
            <LanguageToggleCompact disabled={disabled} />
          </div>
        </div>
      </PopoverContent>
    </Popover>
  )
}


function CtaButton({ className, size = "lg" }) {
  const { cta, busy } = useControlBar()
  const [loading, setLoading] = useState(false)
  const ref = useRef(null)

  // Reset loading state when the CTA descriptor changes (e.g. page navigated)
  useEffect(() => {
    setLoading(false)
  }, [cta?.label])

  const showLoading = loading || busy
  const loadingText = busy ? "Processing…" : (cta?.loadingLabel || "Loading...")

  const button = (
    <Button
      ref={ref}
      size={size}
      className={className}
      variant={cta?.variant || "default"}
      disabled={showLoading || !cta || cta.disabled}
      onClick={() => {
        if (ref.current) ref.current.style.minWidth = `${ref.current.offsetWidth}px`
        setLoading(true)
        cta?.onClick?.()
      }}
    >
      {(showLoading || cta?.spinning) && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
      {cta?.icon && !showLoading && !cta?.spinning && <cta.icon className="h-4 w-4 mr-2" />}
      {showLoading ? loadingText : (cta?.label || "Start Station")}
    </Button>
  )

  if (cta?.tooltip) {
    return (
      <TooltipProvider delayDuration={300}>
        <Tooltip>
          <TooltipTrigger asChild>
            <span className={className}>{button}</span>
          </TooltipTrigger>
          <TooltipContent side="top" sideOffset={8}>{cta.tooltip}</TooltipContent>
        </Tooltip>
      </TooltipProvider>
    )
  }

  return button
}

export function ControlBar() {
  const { cta, isInterviewActive, stopHandler, busy } = useControlBar()

  if (!cta && !isInterviewActive && !busy) return null

  return (
    <div className="border-t bg-muted/20 px-4 py-3 sm:px-6">
      {/* Mobile: simple flex row */}
      <div className="flex items-center justify-between gap-2 sm:hidden">
        <div className="flex items-center gap-1">
          <Button
            variant={isInterviewActive ? "destructive" : "outline"}
            size="icon"
            className="shrink-0 rounded-full"
            disabled={busy || !isInterviewActive}
            onClick={() => stopHandler?.()}
          >
            <ArrowLeft className="h-3.5 w-3.5" />
          </Button>
        </div>
        <CtaButton className="shrink-0" />
        <TooltipProvider delayDuration={300}>
          <StationSettings />
        </TooltipProvider>
      </div>

      {/* Desktop: original centered layout */}
      <div className="relative hidden items-center justify-center sm:flex">
        <CtaButton className="w-48" />
        <div className="absolute right-[calc(50%+6.5rem)]">
          <TooltipProvider delayDuration={300}>
            <div className="flex items-center gap-2">
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant={isInterviewActive ? "destructive" : "outline"}
                    size="icon"
                    className="shrink-0 rounded-full"
                    disabled={busy || !isInterviewActive}
                    onClick={() => stopHandler?.()}
                  >
                    <ArrowLeft className="h-3.5 w-3.5" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="top" sideOffset={8}>Stop and exit station</TooltipContent>
              </Tooltip>
            </div>
          </TooltipProvider>
        </div>
        <div className="absolute left-[calc(50%+6.5rem)]">
          <TooltipProvider delayDuration={300}>
            <div className="flex items-center gap-2">
              <ModeToggleInline />
              <StationSettings variant="durations" />
            </div>
          </TooltipProvider>
        </div>
      </div>
    </div>
  )
}
